import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, FileText, Loader2, CheckCircle, XCircle, ArrowRight, Clock, Eye, X, Search, ShoppingCart, Link2, Edit2 } from 'lucide-react';
import { indentAPI, productAPI, branchAPI, vendorAPI, purchaseAPI, stockAPI, fetchPaged } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import ApprovalStatusBadge from '../ui/ApprovalStatusBadge';
import { DocumentTrail } from '../ui/DocumentTrail';
import Pagination from '../ui/Pagination';
import ProductSearchInput from '../ui/ProductSearchInput';
import VendorSearchInput from '../ui/VendorSearchInput';
import SortableHeader from '../ui/SortableHeader';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';

const statusColors = {
  'Pending': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'PO Issued': 'bg-blue-100 text-blue-700',
  'Fulfilled': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Cancelled': 'bg-red-100 text-red-700',
};

const priorityColors = {
  'High': 'bg-red-100 text-red-700',
  'Medium': 'bg-amber-100 text-amber-700',
  'Low': 'bg-gray-100 text-gray-600',
};

const IndentModule = () => {
  const [indents, setIndents] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showConvertPO, setShowConvertPO] = useState(null);
  const [editingIndent, setEditingIndent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [search, setSearch] = useState('');
  const [linkedItems, setLinkedItems] = useState([]);
  const [stats, setStats] = useState({ total_indents: 0, pending: 0, approved: 0, po_issued: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const { hasPermission } = usePermissions();

  const emptyForm = {
    document_title: '',
    items: [{ product: '', name: '', qty: 1, unit: 'kg', purpose: 'Production' }],
    branch: user.branch || '', required_by: '', purpose: '', priority: 'Medium', notes: '',
  };
  const [form, setForm] = useState({ ...emptyForm });

  const emptyPOForm = {
    vendor: '', vendor_name: '', vendor_address: '', items: [], currency: 'INR', amount: 0, inr_value: 0,
    expected_delivery: '', status: 'Ordered', branch: '', type: 'domestic',
    exchange_rate: 1, notes: '', billing_address: '',
    shipping_address: '', vendor_gst: '', company_gst: '',
    payment_terms: '50% Advance, 50% Against Delivery', delivery_terms: '', reference_no: '', document_title: '', comments: '',
  };
  const [poForm, setPOForm] = useState({ ...emptyPOForm });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (filter) params.status = filter;
      if (sortConfig.key) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.order; }
      const [result, productsData, branchesData, vendorsData, linksData, indentStats] = await Promise.all([
        fetchPaged('/indents', params), productAPI.getAll().catch(() => []),
        branchAPI.getAll().catch(() => []), vendorAPI.getAll().catch(() => []),
        stockAPI.getLinkedItems().catch(() => []), indentAPI.getStats().catch(() => ({})),
      ]);
      setIndents(result.items); setTotal(result.total); setTotalPages(result.total_pages);
      setProducts(productsData); setBranches(branchesData); setVendors(vendorsData); setLinkedItems(linksData);
      setStats(indentStats);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [search, filter, page, pageSize, sortConfig]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateIndent = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate indents
    if (!form.branch) { alert('Please select a branch'); return; }
    if (!form.required_by || !form.purpose || form.items.some(i => !i.name || !i.qty)) {
      alert('Please fill all required fields'); return;
    }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      const branch = branches.find(b => b.id === form.branch);
      const payload = { ...form, branch_name: branch?.name || form.branch };
      if (editingIndent) {
        await indentAPI.update(editingIndent.id, payload);
      } else {
        await indentAPI.create(payload);
      }
      setShowCreate(false); setForm({ ...emptyForm }); setEditingIndent(null); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save indent'); }
    finally { setSubmitting(false); submittingRef.current = false; }
  };

  const openEditIndent = (indent) => {
    if (indent.status !== 'Pending') {
      alert('Only Pending indents can be edited.'); return;
    }
    setForm({
      document_title: indent.document_title || '',
      items: (indent.items || []).map(i => ({ product: i.product || '', name: i.name || '', sku: i.sku || '', qty: i.qty || 1, unit: i.unit || 'kg', purpose: i.purpose || '' })),
      branch: indent.branch || '', required_by: indent.required_by || '',
      purpose: indent.purpose || '', priority: indent.priority || 'Medium', notes: indent.notes || '',
    });
    setEditingIndent(indent);
    setShowCreate(true);
  };

  const handleStatusUpdate = async (indentId, newStatus) => {
    try {
      await indentAPI.update(indentId, { status: newStatus });
      fetchAll();
      if (showDetail?.id === indentId) setShowDetail(null);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update status'); }
  };

  // Convert Indent to PO — only allowed for Approved indents
  const openConvertToPO = async (indent) => {
    if (indent.status !== 'Approved') {
      alert('This indent must be Approved before it can be converted to a Purchase Order. Please get approval first.');
      return;
    }
    // Map indent items to PO items, lookup product details for HSN/GST
    const poItems = await Promise.all((indent.items || []).map(async (item) => {
      let hsn = '', gstRate = 18, desc = '';
      if (item.product) {
        try {
          const prod = await productAPI.getById(item.product);
          hsn = prod.hsn_code || '';
          gstRate = prod.gst_rate || 18;
          desc = prod.description || '';
        } catch { /* ignore lookup failure */ }
      }
      return {
        product: item.product || '', name: item.name, description: desc, qty: item.qty, unit: item.unit,
        rate: 0, amount: 0, hsn_code: hsn, gst_rate: gstRate,
      };
    }));
    let billingAddr = '', companyGst = '';
    // Auto-fetch billing address from branch
    if (indent.branch) {
      try {
        const addr = await branchAPI.getBillingAddress(indent.branch);
        billingAddr = addr.billing_address || '';
        companyGst = addr.company_gst || '';
      } catch (err) { console.error('Failed to fetch branch address:', err); }
    }
    setPOForm({
      ...emptyPOForm, items: poItems, branch: indent.branch || '',
      billing_address: billingAddr, company_gst: companyGst,
      expected_delivery: indent.required_by || '', reference_no: indent.indent_number || indent.id,
      document_title: indent.document_title || '',
      notes: `Generated from Indent: ${indent.indent_number || indent.id} - ${indent.purpose || ''}`,
    });
    setShowConvertPO(indent);
    setShowDetail(null);
  };

  const handleConvertToPO = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate POs
    if (!poForm.vendor || poForm.items.some(i => !i.name || !i.rate)) {
      alert('Please select a vendor and enter rates for all items'); return;
    }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      await indentAPI.convertToPO(showConvertPO.id, poForm);
      setShowConvertPO(null); setPOForm({ ...emptyPOForm }); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to convert to PO'); }
    finally { setSubmitting(false); submittingRef.current = false; }
  };

  const selectVendor = (vendorId) => {
    const v = vendors.find(x => x.id === vendorId);
    if (v) {
      const fullAddress = [v.address, v.city, v.state, v.pincode].filter(Boolean).join(', ');
      setPOForm({ ...poForm, vendor: v.id, vendor_name: v.name, vendor_address: fullAddress || v.location || '', currency: v.currency || 'INR', type: v.type || 'domestic', vendor_gst: v.gstin || '' });
    }
  };

  const handleIndentVendorSelect = (v) => {
    const fullAddress = [v.address, v.city, v.state, v.pincode].filter(Boolean).join(', ');
    setPOForm({ ...poForm, vendor: v.id, vendor_name: v.name, vendor_address: fullAddress || v.location || '', currency: v.currency || 'INR', type: v.type || 'domestic', vendor_gst: v.gstin || '' });
  };

  const handleIndentVendorClear = () => {
    setPOForm({ ...poForm, vendor: '', vendor_name: '', vendor_gst: '', vendor_address: '' });
  };

  const updatePOItem = (idx, field, value) => {
    const items = [...poForm.items];
    items[idx] = { ...items[idx], [field]: field === 'qty' || field === 'rate' || field === 'gst_rate' ? Number(value) : value };
    if (field === 'qty' || field === 'rate') items[idx].amount = items[idx].qty * items[idx].rate;
    const amount = items.reduce((s, i) => s + (i.amount || 0), 0);
    setPOForm({ ...poForm, items, amount, inr_value: amount * (poForm.exchange_rate || 1) });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { product: '', name: '', qty: 1, unit: 'kg', purpose: 'Production' }] });
  const removeItem = (idx) => { if (form.items.length <= 1) return; setForm({ ...form, items: form.items.filter((_, i) => i !== idx) }); };

  const handleProductSelect = (idx, product) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], product: product.id, name: product.name, unit: product.unit || 'kg', sku: product.sku || '', _productData: product };
    setForm({ ...form, items });
  };

  const handleProductClear = (idx) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], product: '', name: '', unit: 'kg', _productData: null };
    setForm({ ...form, items });
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    // If product is selected, don't allow manual UOM change
    if (field === 'unit' && items[idx].product) {
      const prod = items[idx]._productData;
      if (prod) { items[idx].unit = prod.unit || 'kg'; return setForm({ ...form, items }); }
    }
    setForm({ ...form, items });
  };

  const filteredIndents = indents;

  const statCards = { total: stats.total_indents || 0, pending: stats.pending || 0, approved: stats.approved || 0, poIssued: stats.po_issued || 0 };

  // Branch name resolver
  const branchNameMap = {};
  branches.forEach(b => { branchNameMap[b.id] = b.name; });
  const getBranchName = (indent) => indent.branch_name || branchNameMap[indent.branch] || indent.branch;

  // Linked items helper for smart suggestions
  const linkedMap = {};
  linkedItems.forEach(link => {
    if (!linkedMap[link.product_a_id]) linkedMap[link.product_a_id] = [];
    linkedMap[link.product_a_id].push({ id: link.product_b_id, name: link.product_b_name });
    if (!linkedMap[link.product_b_id]) linkedMap[link.product_b_id] = [];
    linkedMap[link.product_b_id].push({ id: link.product_a_id, name: link.product_a_name });
  });

  // Full-page Indent Creation Form
  if (showCreate) {
    return (
      <div className="p-5 lg:p-6" data-testid="indent-form-page">
        <button onClick={() => { setShowCreate(false); setEditingIndent(null); setForm({ ...emptyForm }); }} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 mb-4">
          <X className="w-3.5 h-3.5" /> Back to Indents
        </button>

        <div className="bg-white rounded-xl border border-gray-100">
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>{editingIndent ? 'Edit Material Indent' : 'New Material Indent'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Fill in the details for your material requisition</p>
          </div>

          <div className="p-5 space-y-5">
            {/* Document Title */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Document Title</label>
              <input data-testid="indent-document-title" value={form.document_title || ''} onChange={e => setForm({ ...form, document_title: e.target.value })} placeholder="e.g. Material for NH-48 Phase II" className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>

            {/* Row 1: Branch, Priority, Required By, Purpose */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Branch *</label>
                <select data-testid="indent-branch" value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select Branch</option>
                  {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  {branches.length === 0 && <option value={user.branch}>{user.branch}</option>}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Priority *</label>
                <select data-testid="indent-priority" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Required By *</label>
                <input data-testid="indent-required-by" type="date" value={form.required_by} onChange={e => setForm({ ...form, required_by: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Purpose / Production Ref *</label>
                <input data-testid="indent-purpose" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="e.g., For PRD-001 batch" className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </div>

            {/* Items Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Items *</label>
                <button data-testid="add-indent-item" onClick={addItem} className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors" style={{ color: '#24549c' }}>
                  <Plus className="w-3 h-3" /> Add Item
                </button>
              </div>

              {/* Item Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 rounded-t-lg border border-gray-100 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                <div className="col-span-1">SKU</div>
                <div className="col-span-4">Product</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-2">Unit</div>
                <div className="col-span-3">Purpose</div>
                <div className="col-span-1"></div>
              </div>

              <div className="border border-t-0 border-gray-100 rounded-b-lg divide-y divide-gray-50">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-3 hover:bg-gray-50/50">
                    <div className="col-span-1">
                      <input value={item.sku || ''} readOnly className="w-full px-2 py-2 text-xs border border-gray-200 rounded-md bg-gray-100 text-gray-600 font-mono" placeholder="—" title="SKU auto-filled" />
                    </div>
                    <div className="col-span-4">
                      <ProductSearchInput
                        value={item.name}
                        productId={item.product}
                        onSelect={(p) => handleProductSelect(idx, p)}
                        onChange={(text) => updateItem(idx, 'name', text)}
                        onClear={() => handleProductClear(idx)}
                        placeholder="Search product by name or SKU..."
                        testId={`indent-item-product-${idx}`}
                      />
                      {item.product && linkedMap[item.product] && (
                        <div className="mt-1 flex items-center gap-1 text-[9px] text-blue-600">
                          <Link2 className="w-2.5 h-2.5" />
                          <span>Interchangeable: {linkedMap[item.product].map(l => l.name).join(', ')}</span>
                        </div>
                      )}
                    </div>
                    <div className="col-span-1">
                      <input data-testid={`indent-item-qty-${idx}`} type="number" value={item.qty} onChange={e => updateItem(idx, 'qty', Number(e.target.value))} placeholder="Qty" className="w-full px-2 py-2 text-xs border border-gray-200 rounded-md" />
                    </div>
                    <div className="col-span-2">
                      <select value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} disabled={!!item.product} className={`w-full px-2 py-2 text-xs border border-gray-200 rounded-md ${item.product ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`} title={item.product ? 'UOM is locked to the selected product' : 'Select UOM'}>
                        {['kg', 'pcs', 'sqft', 'sqm', 'MT', 'rolls', 'sets', 'Kg', 'PCS', 'Mtr', 'Sqft', 'Sqm', 'Ltr', 'Roll', 'Drum', 'Set', 'Pkt'].filter((v, i, a) => a.indexOf(v) === i).map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      {item.product && <p className="text-[9px] text-amber-600 mt-0.5">Locked to product UOM</p>}
                    </div>
                    <div className="col-span-3">
                      <input value={item.purpose} onChange={e => updateItem(idx, 'purpose', e.target.value)} placeholder="Purpose for this item" className="w-full px-2 py-2 text-xs border border-gray-200 rounded-md" />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && <button onClick={() => removeItem(idx)} className="p-1.5 rounded-lg hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Notes</label>
              <textarea data-testid="indent-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Additional notes or special instructions..." className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
            <button onClick={() => { setShowCreate(false); setEditingIndent(null); setForm({ ...emptyForm }); }} className="px-5 py-2.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button data-testid="submit-indent-btn" onClick={handleCreateIndent} disabled={submitting} className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{editingIndent ? 'Update Indent' : 'Submit Indent'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 lg:p-6" data-testid="indent-module">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Material Indents</h1>
          <p className="text-xs text-gray-500 mt-0.5">Create material requisitions for procurement workflow</p>
        </div>
        {hasPermission('indent.create') && (
          <button data-testid="create-indent-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 transition-all" style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> New Indent
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Indents', value: statCards.total, color: '#24549c', icon: FileText },
          { label: 'Pending', value: statCards.pending, color: '#E65100', icon: Clock },
          { label: 'Approved', value: statCards.approved, color: '#2E7D32', icon: CheckCircle },
          { label: 'PO Issued', value: statCards.poIssued, color: '#1565C0', icon: ArrowRight },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.value}</p>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="indent-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by indent # or purpose..." className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {['', 'Pending', 'Approved', 'PO Issued', 'Rejected', 'Cancelled'].map(f => (
            <button key={f} data-testid={`indent-filter-${(f || 'all').toLowerCase().replace(' ', '-')}`} onClick={() => { setFilter(f); setPage(1); }} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${filter === f ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} style={filter === f ? { backgroundColor: '#24549c' } : {}}>{f || 'All'}</button>
          ))}
        </div>
      </div>

      {/* Indent List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading indents...</span></div>
      ) : filteredIndents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No indents found</p><p className="text-xs text-gray-400 mt-1">Create your first material requisition to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-gray-100 bg-gray-50">
                <SortableHeader label="Indent #" sortKey="indent_number" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Document Title" />
                <SortableHeader label="Date" sortKey="created_at" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Items" />
                <SortableHeader label="Branch" />
                <SortableHeader label="Priority" />
                <SortableHeader label="Required By" />
                <SortableHeader label="Status" />
                <SortableHeader label="Actions" />
              </tr></thead>
              <tbody>
                {filteredIndents.map(indent => (
                  <tr key={indent.id} data-testid={`indent-row-${indent.id}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-900">{indent.indent_number || indent.id?.substring(0, 10)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-[150px] truncate">{indent.document_title || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{indent.created_at ? new Date(indent.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{indent.items?.length || 0} items</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{getBranchName(indent)}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${priorityColors[indent.priority] || 'bg-gray-100 text-gray-600'}`}>{indent.priority}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{indent.required_by}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[indent.status] || 'bg-gray-100 text-gray-600'}`}>{indent.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`view-indent-${indent.id}`} onClick={() => setShowDetail(indent)} className="p-1.5 rounded-lg hover:bg-gray-100" title="View"><Eye className="w-3.5 h-3.5 text-gray-600" /></button>
                        {hasPermission('indent.create') && indent.status === 'Pending' && (
                          <button data-testid={`edit-indent-${indent.id}`} onClick={() => openEditIndent(indent)} className="p-1.5 rounded-lg hover:bg-blue-50" title="Edit"><Edit2 className="w-3.5 h-3.5 text-blue-600" /></button>
                        )}
                        {hasPermission('indent.manage') && indent.status === 'Pending' && (
                          <>
                            <button data-testid={`approve-indent-${indent.id}`} onClick={() => handleStatusUpdate(indent.id, 'Approved')} className="p-1.5 rounded-lg hover:bg-green-50" title="Approve"><CheckCircle className="w-3.5 h-3.5 text-green-600" /></button>
                            <button data-testid={`reject-indent-${indent.id}`} onClick={() => handleStatusUpdate(indent.id, 'Rejected')} className="p-1.5 rounded-lg hover:bg-red-50" title="Reject"><XCircle className="w-3.5 h-3.5 text-red-500" /></button>
                          </>
                        )}
                        {hasPermission('indent.manage') && indent.status === 'Approved' && !indent.linked_po && (
                          <button data-testid={`convert-po-${indent.id}`} onClick={() => openConvertToPO(indent)} className="p-1.5 rounded-lg hover:bg-blue-50" title="Convert to PO"><ShoppingCart className="w-3.5 h-3.5 text-blue-600" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">Showing {indents.length} of {total} indents</div>
        </div>
      )}

      {indents.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Indent Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Indent Details</h2>
                <p className="text-xs text-gray-500 font-mono">{showDetail.indent_number || showDetail.id}</p>
              </div>
              <button onClick={() => setShowDetail(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Approval Status */}
              <ApprovalStatusBadge documentType="Indent" documentId={showDetail.id} documentNumber={showDetail.indent_number || showDetail.id} onStatusChange={() => fetchAll()} />
              {/* Document Trail */}
              <DocumentTrail docType="indent" docId={showDetail.id} />
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Status</p><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[showDetail.status] || 'bg-gray-100'}`}>{showDetail.status}</span></div>
                <div><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Priority</p><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${priorityColors[showDetail.priority] || 'bg-gray-100'}`}>{showDetail.priority}</span></div>
                <div><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Branch</p><p className="text-sm text-gray-700">{getBranchName(showDetail)}</p></div>
                <div><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Required By</p><p className="text-sm text-gray-700">{showDetail.required_by}</p></div>
                <div className="col-span-2"><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Purpose</p><p className="text-sm text-gray-700">{showDetail.purpose}</p></div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-900 mb-2">Requested Items</p>
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50"><tr><th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">#</th><th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Material</th><th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500">Qty</th><th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Unit</th><th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Purpose</th></tr></thead>
                    <tbody>{showDetail.items?.map((item, idx) => (
                      <tr key={idx} className="border-t border-gray-50"><td className="px-3 py-2">{idx + 1}</td><td className="px-3 py-2 font-medium">{item.name}</td><td className="px-3 py-2 text-right font-semibold">{item.qty}</td><td className="px-3 py-2">{item.unit}</td><td className="px-3 py-2 text-gray-500">{item.purpose}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
              {showDetail.notes && <div><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Notes</p><p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">{showDetail.notes}</p></div>}
              {showDetail.linked_po && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <ArrowRight className="w-4 h-4 text-blue-600" /><p className="text-xs text-blue-700">Linked PO: <span className="font-mono font-semibold">{showDetail.linked_po}</span></p>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                {hasPermission('indent.manage') && showDetail.status === 'Pending' && (
                  <>
                    <button data-testid="detail-approve-btn" onClick={() => { handleStatusUpdate(showDetail.id, 'Approved'); }} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700"><CheckCircle className="w-3.5 h-3.5" /> Approve</button>
                    <button data-testid="detail-reject-btn" onClick={() => { handleStatusUpdate(showDetail.id, 'Rejected'); }} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600"><XCircle className="w-3.5 h-3.5" /> Reject</button>
                  </>
                )}
                {hasPermission('indent.manage') && showDetail.status === 'Approved' && !showDetail.linked_po && (
                  <button data-testid="detail-convert-po-btn" onClick={() => openConvertToPO(showDetail)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
                    <ShoppingCart className="w-3.5 h-3.5" /> Convert to PO
                  </button>
                )}
                {hasPermission('indent.manage') && ['Pending', 'Approved'].includes(showDetail.status) && (
                  <button data-testid="detail-cancel-indent-btn" onClick={async () => {
                    if (!window.confirm('Are you sure you want to cancel this indent?')) return;
                    try {
                      await indentAPI.cancel(showDetail.id);
                      toast.success('Indent cancelled successfully');
                      setShowDetail(null);
                      fetchAll();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to cancel indent');
                    }
                  }} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100">
                    <XCircle className="w-3.5 h-3.5" /> Cancel Indent
                  </button>
                )}
                <button onClick={() => setShowDetail(null)} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 ml-auto">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert to PO Modal */}
      {showConvertPO && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Convert Indent to Purchase Order</h2>
                <p className="text-xs text-gray-500">Source: <span className="font-mono font-semibold">{showConvertPO.indent_number || showConvertPO.id}</span> &mdash; {showConvertPO.purpose}</p>
              </div>
              <button onClick={() => setShowConvertPO(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Vendor selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Vendor *</label>
                  <VendorSearchInput
                    value={poForm.vendor_name}
                    vendorId={poForm.vendor}
                    onSelect={handleIndentVendorSelect}
                    onChange={(text) => setPOForm({ ...poForm, vendor_name: text })}
                    onClear={handleIndentVendorClear}
                    testId="convert-po-vendor"
                  /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Delivery Date *</label>
                  <input data-testid="convert-po-delivery" type="date" value={poForm.expected_delivery} onChange={e => setPOForm({ ...poForm, expected_delivery: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Payment Terms</label>
                  <input value={poForm.payment_terms} onChange={e => setPOForm({ ...poForm, payment_terms: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Vendor GSTIN</label>
                  <input value={poForm.vendor_gst} onChange={e => setPOForm({ ...poForm, vendor_gst: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Delivery Terms (FOR/EXW) *</label>
                  <select data-testid="convert-po-delivery-terms" value={poForm.delivery_terms} onChange={e => setPOForm({ ...poForm, delivery_terms: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Select Delivery Terms</option>
                    <option value="FOR Destination">FOR Destination</option>
                    <option value="FOR Factory">FOR Factory</option>
                    <option value="EXW">EXW (Ex Works)</option>
                    <option value="CIF">CIF</option>
                    <option value="FOB">FOB</option>
                  </select></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Vendor Address</label>
                  <textarea data-testid="convert-po-vendor-address" value={poForm.vendor_address} onChange={e => setPOForm({ ...poForm, vendor_address: e.target.value })} rows={2} placeholder="Auto-filled from vendor master" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Billing Address</label>
                  <textarea value={poForm.billing_address} onChange={e => setPOForm({ ...poForm, billing_address: e.target.value })} rows={2} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none" placeholder="Auto-filled from branch" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Shipping Address</label>
                  <textarea value={poForm.shipping_address} onChange={e => setPOForm({ ...poForm, shipping_address: e.target.value })} rows={2} placeholder="Leave blank if same as billing" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none" /></div>
              </div>
              {/* Items with rates */}
              <div>
                <p className="text-xs font-semibold text-gray-900 mb-2">Items (enter unit rates to generate PO values)</p>
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Material</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Description</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500">Qty</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Unit</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500">Rate *</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500">HSN</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500">Amount</th>
                    </tr></thead>
                    <tbody>
                      {poForm.items.map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-50">
                          <td className="px-3 py-2 font-medium">{item.name}</td>
                          <td className="px-3 py-2"><input data-testid={`convert-po-desc-${idx}`} value={item.description || ''} onChange={e => updatePOItem(idx, 'description', e.target.value)} className="w-28 px-2 py-1 text-xs border border-gray-200 rounded-md" placeholder="Specs" /></td>
                          <td className="px-3 py-2 text-right">{item.qty}</td>
                          <td className="px-3 py-2">{item.unit}</td>
                          <td className="px-3 py-2"><input data-testid={`convert-po-rate-${idx}`} type="number" value={item.rate} onChange={e => updatePOItem(idx, 'rate', e.target.value)} className="w-20 px-2 py-1 text-xs border border-gray-200 rounded-md text-right" placeholder="0" /></td>
                          <td className="px-3 py-2"><input value={item.hsn_code} onChange={e => updatePOItem(idx, 'hsn_code', e.target.value)} className="w-16 px-2 py-1 text-xs border border-gray-200 rounded-md" placeholder="HSN" /></td>
                          <td className="px-3 py-2 text-right font-semibold">{(item.amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-2">
                  <div className="bg-gray-50 rounded-lg px-4 py-2 text-right">
                    <p className="text-xs text-gray-500">PO Total: <span className="font-bold text-gray-900 text-sm">{poForm.currency} {poForm.amount.toLocaleString()}</span></p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowConvertPO(null)} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button data-testid="submit-convert-po-btn" onClick={handleConvertToPO} disabled={submitting} className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <ShoppingCart className="w-3.5 h-3.5" /> Create PO from Indent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndentModule;
